import _ from 'lodash-es';
import { RegistryTypes } from 'Portainer/models/registryTypes';
import { getTagDetails } from '@/react/portainer/registries/repositories/queries/useTagDetails';
import { ping, pingWithForceNew } from '@/react/portainer/registries/queries/useRegistryPing';
import { getAllRepositories } from '@/react/portainer/registries/registry.service';
import { getRepositoryTags } from '@/react/portainer/registries/repositories/queries/useRepositoryTags';
import { RepositoryAddTagPayload, RepositoryShortTag } from '../models/repositoryTag';
import { RegistryRepositoryViewModel } from '../models/registryRepository';
import genericAsyncGenerator from './genericAsyncGenerator';

angular.module('portainer.registrymanagement').factory('RegistryV2Service', RegistryV2ServiceFactory);

/* @ngInject */
function RegistryV2ServiceFactory($q, RegistryManifestsJquery, AngularToReact) {
  const { useAxios } = AngularToReact;
  return {
    ping: useAxios(pingAngularJS),
    repositories: useAxios(repositoriesAngularJS),
    getRepositoriesDetails: useAxios(getRepositoriesDetailsAngularJS),
    tags: useAxios(tagsAngularJS),
    tag: useAxios(tagAngularJS),
    getTagsDetails: useAxios(getTagsDetailsAngularJS),
    shortTagsWithProgress,
    addTag,
    deleteManifest,
    deleteTagsWithProgress,
    retagWithProgress,
  };

  /**
   * PING
   */
  async function pingAngularJS(registry, endpointId, forceNewConfig) {
    const id = registry.Id;
    if (forceNewConfig) {
      return pingWithForceNew(id, endpointId);
    }
    return ping(id, endpointId);
  }

  /**
   * END PING
   */

  /**
   * REPOSITORIES
   */

  async function repositoriesAngularJS(registry, endpointId) {
    const id = registry.Id;
    const repositories = await getAllRepositories(id, endpointId);
    return _.map(repositories, (repository) => new RegistryRepositoryViewModel(repository.Name));
  }

  async function getRepositoriesDetailsAngularJS(registry, endpointId, repositories) {
    const promises = _.map(repositories, (repository) => tagsAngularJS(registry, endpointId, repository.Name));
    const results = await Promise.allSettled(promises);

    const repositoryDetails = results.map(function (item) {
      if (item.status === 'fulfilled') {
        return new RegistryRepositoryViewModel(item.value);
      } else {
        const name = _.get(item, 'reason.err.data.errors[0].detail[0].Name');
        return new RegistryRepositoryViewModel(name);
      }
    });

    return _.without(repositoryDetails, undefined);
  }

  /**
   * END REPOSITORIES
   */

  /**
   * TAGS
   */

  async function tagsAngularJS(registry, endpointId, repository) {
    const id = registry.Id;
    try {
      const result = await getRepositoryTags({
        environmentId: endpointId,
        registryId: id,
        repository: repository,
      });
      return result;
    } catch (err) {
      if (err.status === 404) {
        return { name: repository, tags: [] };
      }
      throw {
        msg: 'Unable to retrieve tags',
        err: err,
      };
    }
  }

  async function getTagsDetailsAngularJS(registry, endpointId, repository, tags) {
    const promises = _.map(tags, (t) => tagAngularJS(registry, endpointId, repository, t.Name));
    return Promise.all(promises);
  }

  async function tagAngularJS(registry, environmentId, repository, tag) {
    return getTagDetails({
      registryId: registry.Id,
      environmentId,
      repository,
      tag,
    });
  }

  /**
   * END TAGS
   */

  /**
   * ADD TAG
   */

  // tag: RepositoryAddTagPayload
  function _addTagFromGenerator(registry, endpointId, repository, tag) {
    return addTag(registry, endpointId, repository, tag.Tag, tag.Manifest);
  }

  function addTag(registry, endpointId, repository, tag, manifest) {
    const id = registry.Id;
    delete manifest.digest;
    return RegistryManifestsJquery.put(
      {
        id: id,
        endpointId: endpointId,
        repository: repository,
        tag: tag,
      },
      manifest
    );
  }

  async function* _addTagsWithProgress(registry, endpointId, repository, tagsList, progression = 0) {
    // ghcr.io does not support adding multiple tags simultaneity
    const step = registry.Type === RegistryTypes.GITHUB ? 1 : undefined;
    for await (const partialResult of genericAsyncGenerator($q, tagsList, _addTagFromGenerator, [registry, endpointId, repository], step)) {
      if (typeof partialResult === 'number') {
        yield progression + partialResult;
      } else {
        yield partialResult;
      }
    }
  }

  /**
   * END ADD TAG
   */

  /**
   * DELETE MANIFEST
   */

  function deleteManifest(registry, endpointId, repository, imageDigest) {
    const id = registry.Id;
    return RegistryManifestsJquery.delete({
      id: id,
      endpointId: endpointId,
      repository: repository,
      tag: imageDigest,
    });
  }

  async function* _deleteManifestsWithProgress(registry, endpointId, repository, manifests) {
    // ghcr.io does not support deleting multiple manifests simultaneity
    const step = registry.Type === RegistryTypes.GITHUB ? 1 : undefined;
    for await (const partialResult of genericAsyncGenerator($q, manifests, deleteManifest, [registry, endpointId, repository], step)) {
      yield partialResult;
    }
  }

  /**
   * END DELETE MANIFEST
   */

  /**
   * SHORT TAG
   */

  function _shortTagFromGenerator(id, endpointId, repository, tag) {
    return new Promise((resolve, reject) => {
      RegistryManifestsJquery.getV2({ id: id, endpointId: endpointId, repository: repository, tag: tag })
        .then((data) => resolve(new RepositoryShortTag(tag, data.config.digest, data.digest, data)))
        .catch((err) => reject(err));
    });
  }

  async function* shortTagsWithProgress(registry, endpointId, repository, tagsList) {
    const id = registry.Id;
    yield* genericAsyncGenerator($q, tagsList, _shortTagFromGenerator, [id, endpointId, repository]);
  }

  /**
   * END SHORT TAG
   */

  /**
   * RETAG
   *
   * @param {import('@/react/portainer/registries/types').Registry} registry
   * @param {import('@/react/portainer/environments/types').EnvironmentId} [endpointId]
   * @param {string} repository
   * @param {Record<string, import('@/portainer/registry-management/models/repositoryTag').RepositoryTagViewModel>} modifiedTags
   * @param {Array<string>} modifiedDigests
   * @param {Array<import('@/portainer/registry-management/models/repositoryTag').RepositoryShortTag>} impactedTags
   */
  async function* retagWithProgress(registry, endpointId, repository, modifiedTags, modifiedDigests, impactedTags) {
    yield* _deleteManifestsWithProgress(registry, endpointId, repository, modifiedDigests);

    const newTags = _.map(impactedTags, (item) => {
      const tagFromTable = modifiedTags[item.Name];
      const name = tagFromTable && item.Name !== tagFromTable.Name ? tagFromTable.Name : item.Name;
      return new RepositoryAddTagPayload(name, item.ManifestV2);
    });
    yield* _addTagsWithProgress(registry, endpointId, repository, newTags, modifiedDigests.length);
  }

  /**
   * END RETAG
   */

  /**
   * DELETE TAGS
   */

  async function* deleteTagsWithProgress(registry, endpointId, repository, modifiedDigests, impactedTags) {
    yield* _deleteManifestsWithProgress(registry, endpointId, repository, modifiedDigests);

    const newTags = _.map(impactedTags, (item) => new RepositoryAddTagPayload(item.Name, item.ManifestV2));

    yield* _addTagsWithProgress(registry, endpointId, repository, newTags, modifiedDigests.length);
  }

  /**
   * END DELETE TAGS
   */
}
